// src/component/legacymagnet/CustomizeModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import { CloseIcon } from '../../public/icons';
import {
  LegacyBook1,
  LegacyBook2,
  LegacyColorBook,
  LegacyColorBook2
} from '../../public/images/legacy';

const STEP_DRAFT_KEY = 'kapta_legacy_step_customize';

const loadStepDraft = () => {
  try {
    const raw = localStorage.getItem(STEP_DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    console.error('Failed to load customize step draft:', err);
    return null;
  }
};

const saveStepDraft = (data) => {
  try {
    localStorage.setItem(STEP_DRAFT_KEY, JSON.stringify(data));
  } catch (err) {
    console.error('Failed to save customize step draft:', err);
  }
};

const CustomizeModal = ({ isOpen, onClose, onContinue, onProgressChange }) => {
  const [selectedSet, setSelectedSet] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [personalNote, setPersonalNote] = useState('');

  const draftHydratedRef = useRef(false);

  /* ───────── HYDRATE on open ───────── */
  useEffect(() => {
    if (!isOpen) return;

    const draft = loadStepDraft();
    if (!draft) {
      draftHydratedRef.current = true;
      return;
    }

    if (typeof draft.setSize === 'string') setSelectedSet(draft.setSize);
    if (typeof draft.color === 'string') setSelectedColor(draft.color);
    if (typeof draft.personalNote === 'string')
      setPersonalNote(draft.personalNote);

    draftHydratedRef.current = true;
  }, [isOpen]);

  /* ───────── AUTOSAVE + broadcast on every change ───────── */
  useEffect(() => {
    if (!isOpen) return;
    if (!draftHydratedRef.current) return;

    const draft = {
      setSize: selectedSet,
      color: selectedColor,
      personalNote: personalNote.trim()
    };

    saveStepDraft(draft);
    onProgressChange?.(draft);
  }, [isOpen, selectedSet, selectedColor, personalNote, onProgressChange]);

  if (!isOpen) return null;

  const handleContinue = () => {
    const customizationData = {
      setSize: selectedSet,
      color: selectedColor,
      personalNote: personalNote.trim()
    };
    onContinue(customizationData);
    onClose();
  };

  const isContinueEnabled = selectedSet && selectedColor;

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[100] transition-opacity duration-300"
        onClick={onClose}
      />

      <div className="fixed right-0 top-0 h-full w-full md:w-[60%] lg:w-[50%] xl:w-[40%] bg-[#F9F6F2] z-[999] overflow-y-auto transition-transform duration-300 ease-in-out">
        <div className="h-full flex flex-col">
          <div className="flex items-center justify-between mx-6 mb-4 mt-6 pb-2 border-b-2 border-[#DBDBDB]">
            <div className="text-left">
              <h2 className="text-2xl font-bold text-[#1C2A46]">
                Customise Your Legacy Magnets
              </h2>
              <p className="text-[#6B635D] text-sm mt-1">
                Please select all required options
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-[#F8F8F8] border border-[#DBDBDB] rounded"
            >
              <CloseIcon className="w-6 h-6 text-[#1C2A46]" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            <div className="mb-8">
              <h3 className="text-lg text-left font-medium text-[#1C2A46] mb-4">
                Choose Legacy Magnet set*
              </h3>
              <div className="flex gap-4">
                <button
                  onClick={() => setSelectedSet('12')}
                  className={`flex-1 pt-1 px-1 border-2 text-center transition-colors ${
                    selectedSet === '12'
                      ? 'border-[#55000C] '
                      : 'border-[#E4E4E4] hover:border-[#55000C]'
                  }`}
                >
                  <img src={LegacyBook1} alt="Set of 12 Legacy Book" />
                  <div className="flex justify-between items-center mt-3">
                    <div className="text-lg font-medium text-[#1C2A46] mb-1">
                      Set of 12
                    </div>
                    <div className="text-[#389B51] font-normal">
                      ₦300,000
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => setSelectedSet('16')}
                  className={`flex-1 pt-1 px-1 border-2 text-center transition-colors ${
                    selectedSet === '16'
                      ? 'border-[#55000C] '
                      : 'border-[#E4E4E4] hover:border-[#55000C]'
                  }`}
                >
                  <img src={LegacyBook2} alt="Set of 16 Legacy Book" />
                  <div className="flex justify-between items-center mt-3">
                    <div className="text-lg font-medium text-[#1C2A46] mb-1">
                      Set of 16
                    </div>
                    <div className="text-[#389B51] font-normal">
                      ₦325,000
                    </div>
                  </div>
                </button>
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg text-left font-medium text-[#1C2A46] mb-4">
                Choose your Colour*
              </h3>
              <div className="flex gap-4">
                <button
                  onClick={() => setSelectedColor('wine')}
                  className={`flex-1 pt-1 px-1 border-2 text-center transition-colors ${
                    selectedColor === 'wine'
                      ? 'border-[#55000C] '
                      : 'border-[#E4E4E4] hover:border-[#55000C]'
                  }`}
                >
                  <img src={LegacyColorBook} alt="Wine Color Legacy Book" />
                  <div className="flex justify-between items-center mt-3">
                    <div className="text-lg font-medium text-[#1C2A46] mb-1">
                      Wine
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => setSelectedColor('midnight-blue')}
                  className={`flex-1 pt-1 px-1 border-2 text-center transition-colors ${
                    selectedColor === 'midnight-blue'
                      ? 'border-[#55000C] '
                      : 'border-[#E4E4E4] hover:border-[#55000C]'
                  }`}
                >
                  <img
                    src={LegacyColorBook2}
                    alt="Midnight Blue Color Legacy Book"
                  />
                  <div className="flex justify-between items-center mt-3">
                    <div className="text-lg font-medium text-[#1C2A46] mb-1">
                      Midnight Blue
                    </div>
                  </div>
                </button>
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg text-left font-medium text-[#1C2A46] mb-4">
                Add a Note*
              </h3>
              <textarea
                placeholder="Write a personal message that will be included in the Legacy Book."
                value={personalNote}
                onChange={(e) => setPersonalNote(e.target.value)}
                className="w-full h-32 px-4 py-4 border border-[#E4E4E4] bg-[#F5F5F5] text-[#6B635D] focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent placeholder-[#666666] placeholder:font-light resize-none"
              />
              <p className="text-sm text-[#6B635D] mt-3">
                Tell the story behind your memory. It could be a sincere "I love
                you," a heartfelt "thank you for being here," or even a message
                full of longing.
              </p>
            </div>
          </div>

          <div className="p-6">
            <button
              onClick={handleContinue}
              disabled={!isContinueEnabled}
              className={`w-full py-3 font-medium transition-colors ${
                isContinueEnabled
                  ? 'bg-[#D2A146] text-white hover:bg-[#C49660]'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CustomizeModal;